import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';

import {
  catchError,
  defer,
  finalize,
  from,
  retry,
  switchMap,
  throwError,
  timer,
} from 'rxjs';

import { MsalService } from '@azure/msal-angular';
import {
  AuthenticationResult,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { SnakebarService } from '../services/snakebar-service';
import { SpinnerService } from '../services/spinner-service';
import { environment } from '../../environments/environment';

const AUTH_SCOPES = ['user.read', 'openid', 'profile'];
const MAX_RETRY_ATTEMPTS = 2;
const RETRYABLE_STATUS_CODES = [0, 502, 503, 504];

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
  const msalService = inject(MsalService);
  const snakebarService = inject(SnakebarService);
  const spinnerService = inject(SpinnerService);
  const router = inject(Router);
  const isApiRequest = isBackendApiRequest(req.url);

  if (!isApiRequest) {
    return next(req);
  }

  const loadingMessage = getApiLoadingMessageKey(
    req.method,
    req.url,
    router.url,
  );

  let authReq = req;
  const forwardRequest = () =>
    next(authReq).pipe(
      retry({
        count: MAX_RETRY_ATTEMPTS,
        delay: (error: unknown, retryCount: number) => {
          if (isTransientHttpError(error)) {
            return timer(retryCount * 300);
          }

          return throwError(() => error);
        },
      }),
      catchError((error: HttpErrorResponse) => {
        const errorMessage = getHttpErrorMessage(error);

        if (error.status === 401) {
          // Hard reset auth flow on unauthorized API response.
          void msalService.logoutRedirect();
        }

        snakebarService.showErrorMessage(errorMessage);
        return throwError(() => error);
      }),
    );

  const account = msalService.instance.getActiveAccount() ?? undefined;

  const stream$ = account
    ? from(msalService.instance.initialize()).pipe(
        switchMap(() =>
          from(
            msalService.instance.acquireTokenSilent({
              scopes: AUTH_SCOPES,
              account,
            }),
          ),
        ),
        // Only token-acquisition failures are handled here; request errors are
        // handled by forwardRequest()'s own catchError below.
        catchError((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            snakebarService.showErrorMessage(
              'Your session has expired. Redirecting to sign in.',
            );
            void msalService.instance.acquireTokenRedirect({
              scopes: AUTH_SCOPES,
              account,
            });
            return throwError(() => error);
          }

          snakebarService.showErrorMessage(
            'Authentication temporarily unavailable. Please retry.',
          );
          return throwError(() => error);
        }),
        switchMap((result: AuthenticationResult) => {
          const token = result.accessToken;
          if (token) {
            authReq = req.clone({
              headers: req.headers.set('Authorization', `Bearer ${token}`),
            });
          }
          return forwardRequest();
        }),
      )
    : forwardRequest();

  // Surface an endpoint-specific message on the loader while this request runs.
  return defer(() => {
    spinnerService.pushApiMessage(loadingMessage);
    return stream$;
  }).pipe(finalize(() => spinnerService.popApiMessage(loadingMessage)));
};

/** Maps an API request to a translation key shown on the global loader. */
function getApiLoadingMessageKey(
  method: string,
  url: string,
  routeUrl: string,
): string {
  const segment = url.split('?')[0].split('/').pop()?.toLowerCase() ?? '';

  // Writes are page-independent -> always "saving".
  if (
    /^(save|update|insert|upsert|delete|create|add|remove|submit|approve|reject|toggle|activate|deactivate|assign|unassign|import)/.test(
      segment,
    )
  ) {
    return 'common.loading.saving';
  }

  // Reads: prefer the current page (shared endpoints like GetMasterData are
  // used across pages), then fall back to the endpoint, then a generic key.
  const route = routeUrl.toLowerCase();
  const routeRules: [RegExp, string][] = [
    [/dashboardcharts/, 'charts'],
    [/changetrackerreport/, 'changeTracker'],
    [/report/, 'reports'],
    [/addposition|editposition/, 'positions'],
    [/masterdata/, 'data'],
    [/myprofile|profile/, 'profile'],
    [/manageuser/, 'users'],
    [/syncdata/, 'syncJobs'],
    [/dashboard/, 'dashboard'],
  ];
  const endpointRules: [RegExp, string][] = [
    [/change.*report|swprequestchange/, 'changeTracker'],
    [/chart|insight|aggregat/, 'charts'],
    [/report|export/, 'reports'],
    [/profile/, 'profile'],
    [/sync/, 'syncJobs'],
    [/swprequest|position/, 'positions'],
    [/user|role/, 'users'],
    [
      /master|getall|lookup|list|filter|type|language|country|city|state|segment|division|department|skill|notification/,
      'data',
    ],
  ];

  const key =
    routeRules.find(([pattern]) => pattern.test(route))?.[1] ??
    endpointRules.find(([pattern]) => pattern.test(segment))?.[1] ??
    (method.toUpperCase() === 'GET' ? 'default' : 'data');
  return `common.loading.${key}`;
}

function getHttpErrorMessage(error: HttpErrorResponse): string {
  switch (error.status) {
    case 401:
      return 'Unauthorized access';
    case 403:
      return 'Access forbidden';
    case 404:
      return 'Resource not found';
    case 500:
      return 'Internal server error';
    default:
      return error.error?.message || 'Unknown error occurred';
  }
}

function isBackendApiRequest(url: string): boolean {
  return url.startsWith(environment.apiUrl);
}

function isTransientHttpError(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) {
    return false;
  }

  return RETRYABLE_STATUS_CODES.includes(error.status);
}
